import type { TFunction } from "@/i18n/catalog";

/**
 * Display-only German for the food vocabulary (POS2-1134). Nothing here is written back — a
 * German catalogue still holds whatever names its organisation typed, and this changes only
 * what a reader is shown.
 *
 * Applied to categories, subcategories **and** item names. The fixed part of the vocabulary is
 * the taxonomy — the 191-row `food_categories` tree (`20260330193222_inventory_schema.sql` plus
 * the alcohol subcategories) and the classifier's own §8 spec labels, which `waste_events.py`
 * falls back to when an event carries no category FK. Item names have no fixed set at all: a
 * catalogue row is an organisation's own text and the AI's `item` label is open vocabulary
 * (POS2-75). So an item named for something this map knows — "Beef", "Milk" — reads in the
 * reader's language, and anything else reads exactly as stored. Pass-through is the whole
 * mechanism; there is no partial or guessed translation.
 *
 * **Keyed on the English name, not on a slug.** Every other reference map in this codebase keys
 * off a stable slug, and `food_categories` has one — but the API flattens the join to
 * `category_name` / `subcategory_name` and sends no slug, so the name is the only handle a
 * client holds. Exposing the slug is a backend change; this is deliberately not that.
 *
 * Literal keys in a lookup table, never a key assembled at runtime — `check-i18n.mjs` resolves
 * keys statically, so a built key is invisible to it.
 */
export const FOOD_LABEL_KEYS: Record<string, string> = {
  "Fresh Meat & Poultry": "reference.food.fresh-meat-poultry",
  "Fresh Fish & Seafood": "reference.food.fresh-fish-seafood",
  "Fresh Vegetables": "reference.food.fresh-vegetables",
  "Fresh Fruit": "reference.food.fresh-fruit",
  "Dairy & Eggs": "reference.food.dairy-eggs",
  "Bakery & Bread": "reference.food.bakery-bread",
  "Dry Store / Ambient Goods": "reference.food.dry-store-ambient-goods",
  "Sauces, Dressings & Condiments": "reference.food.sauces-dressings-condiments",
  "Herbs, Salads & Deli": "reference.food.herbs-salads-deli",
  "Frozen Foods": "reference.food.frozen-foods",
  "Cereals & Breakfast": "reference.food.cereals-breakfast",
  "Dessert & Baking Supplies": "reference.food.dessert-baking-supplies",
  "Beverages - Cold": "reference.food.beverages-cold",
  "Beverages - Hot": "reference.food.beverages-hot",
  "Prepared / Convenience Foods": "reference.food.prepared-convenience-foods",
  "Plant-Based & Free-From": "reference.food.plant-based-free-from",
  "International Ingredients": "reference.food.international-ingredients",
  "Snacks & Confectionery": "reference.food.snacks-confectionery",
  "Miscellaneous / Specialist": "reference.food.miscellaneous-specialist",
  "Non-Food & Disposables": "reference.food.non-food-disposables",
  Chicken: "reference.food.chicken",
  Turkey: "reference.food.turkey",
  Beef: "reference.food.beef",
  Pork: "reference.food.pork",
  Sausages: "reference.food.sausages",
  "Bacon & Gammon": "reference.food.bacon-gammon",
  "Ham & Cooked Meats": "reference.food.ham-cooked-meats",
  Lamb: "reference.food.lamb",
  "Duck & Game": "reference.food.duck-game",
  Offal: "reference.food.offal",
  Charcuterie: "reference.food.charcuterie",
  Pâté: "reference.food.pate",
  "White Fish": "reference.food.white-fish",
  "Oily Fish": "reference.food.oily-fish",
  "Smoked Fish": "reference.food.smoked-fish",
  "Shellfish & Crustaceans": "reference.food.shellfish-crustaceans",
  "Prepared Seafood": "reference.food.prepared-seafood",
  "Tinned Fish": "reference.food.tinned-fish",
  "Other Seafood": "reference.food.other-seafood",
  Potatoes: "reference.food.potatoes",
  "Onion Family": "reference.food.onion-family",
  "Root Vegetables": "reference.food.root-vegetables",
  Brassicas: "reference.food.brassicas",
  "Squash & Courgettes": "reference.food.squash-courgettes",
  "Peppers & Chillies": "reference.food.peppers-chillies",
  Mushrooms: "reference.food.mushrooms",
  Tomatoes: "reference.food.tomatoes",
  "Salad Vegetables": "reference.food.salad-vegetables",
  Sweetcorn: "reference.food.sweetcorn",
  "Beans & Peas": "reference.food.beans-peas",
  "Other Fresh Veg": "reference.food.other-fresh-veg",
  "Jarred & Preserved": "reference.food.jarred-preserved",
  "Tinned Vegetables": "reference.food.tinned-vegetables",
  "Everyday Fruit": "reference.food.everyday-fruit",
  Citrus: "reference.food.citrus",
  Berries: "reference.food.berries",
  Grapes: "reference.food.grapes",
  "Stone Fruit": "reference.food.stone-fruit",
  Tropical: "reference.food.tropical",
  Melon: "reference.food.melon",
  "Other Fresh Fruit": "reference.food.other-fresh-fruit",
  "Dried Fruit": "reference.food.dried-fruit",
  "Tinned & Prepared Fruit": "reference.food.tinned-prepared-fruit",
  Milk: "reference.food.milk",
  Cream: "reference.food.cream",
  "Butter & Spreads": "reference.food.butter-spreads",
  "Hard Cheese": "reference.food.hard-cheese",
  "Soft Cheese": "reference.food.soft-cheese",
  Eggs: "reference.food.eggs",
  "Yoghurt & Desserts": "reference.food.yoghurt-desserts",
  "Sliced Bread": "reference.food.sliced-bread",
  "Artisan Loaves": "reference.food.artisan-loaves",
  "Baguettes & Sticks": "reference.food.baguettes-sticks",
  "Rolls & Buns": "reference.food.rolls-buns",
  "Wraps & Flatbreads": "reference.food.wraps-flatbreads",
  Pastries: "reference.food.pastries",
  "Crackers & Crispbreads": "reference.food.crackers-crispbreads",
  "Breadcrumbs & Coating": "reference.food.breadcrumbs-coating",
  "Garlic Bread": "reference.food.garlic-bread",
  Pasta: "reference.food.pasta",
  Noodles: "reference.food.noodles",
  "Rice & Grains": "reference.food.rice-grains",
  "Flour & Baking": "reference.food.flour-baking",
  "Tinned Tomatoes": "reference.food.tinned-tomatoes",
  "Tinned Beans & Pulses": "reference.food.tinned-beans-pulses",
  "Tinned Soup": "reference.food.tinned-soup",
  Oils: "reference.food.oils",
  Vinegars: "reference.food.vinegars",
  "Coconut Products": "reference.food.coconut-products",
  "Stock & Gravy": "reference.food.stock-gravy",
  "Sugar & Sweeteners": "reference.food.sugar-sweeteners",
  "Jams & Spreads": "reference.food.jams-spreads",
  "Salt & Pepper": "reference.food.salt-pepper",
  Spices: "reference.food.spices",
  "Curry Pastes": "reference.food.curry-pastes",
  "Ambient Condiments": "reference.food.ambient-condiments",
  "Dried Goods": "reference.food.dried-goods",
  "Oats & Cereals": "reference.food.oats-cereals",
  "Table Sauces": "reference.food.table-sauces",
  "Accompaniment Sauces": "reference.food.accompaniment-sauces",
  "Chutneys & Pickles": "reference.food.chutneys-pickles",
  Dressings: "reference.food.dressings",
  "Pasta & Cooking Sauces": "reference.food.pasta-cooking-sauces",
  "White & Brown Sauces": "reference.food.white-brown-sauces",
  "Asian Sauces": "reference.food.asian-sauces",
  "Curry Sauces": "reference.food.curry-sauces",
  "World Sauces": "reference.food.world-sauces",
  "Fresh Herbs": "reference.food.fresh-herbs",
  Aromatics: "reference.food.aromatics",
  "Prepared Salads": "reference.food.prepared-salads",
  "Deli Dips & Spreads": "reference.food.deli-dips-spreads",
  "Pickled & Fermented": "reference.food.pickled-fermented",
  Antipasti: "reference.food.antipasti",
  "Frozen Vegetables": "reference.food.frozen-vegetables",
  "Frozen Fruit": "reference.food.frozen-fruit",
  "Frozen Chips & Potatoes": "reference.food.frozen-chips-potatoes",
  "Frozen Savoury": "reference.food.frozen-savoury",
  "Frozen Pastry": "reference.food.frozen-pastry",
  "Frozen Bakery": "reference.food.frozen-bakery",
  "Frozen Desserts": "reference.food.frozen-desserts",
  "Frozen Ice Cream": "reference.food.frozen-ice-cream",
  Cereals: "reference.food.cereals",
  "Breakfast Batters & Mixes": "reference.food.breakfast-batters-mixes",
  "Breakfast Accompaniments": "reference.food.breakfast-accompaniments",
  "Breakfast Bars": "reference.food.breakfast-bars",
  "Bake-Off Pastries": "reference.food.bake-off-pastries",
  "Prepared Breakfast": "reference.food.prepared-breakfast",
  "Extracts & Flavourings": "reference.food.extracts-flavourings",
  "Setting Agents": "reference.food.setting-agents",
  "Custard & Dessert Mixes": "reference.food.custard-dessert-mixes",
  "Pastry Cases & Meringue": "reference.food.pastry-cases-meringue",
  "Icing & Decoration": "reference.food.icing-decoration",
  "Nuts & Seeds": "reference.food.nuts-seeds",
  "Chocolate & Sauces": "reference.food.chocolate-sauces",
  Water: "reference.food.water",
  Juices: "reference.food.juices",
  Smoothies: "reference.food.smoothies",
  "Soft Drinks": "reference.food.soft-drinks",
  "Energy & Sports Drinks": "reference.food.energy-sports-drinks",
  "Mixers & Cordials": "reference.food.mixers-cordials",
  "Iced & Wellness Drinks": "reference.food.iced-wellness-drinks",
  "Barista Alt Milks": "reference.food.barista-alt-milks",
  "Flavoured Milk": "reference.food.flavoured-milk",
  "Coffee Beans & Ground": "reference.food.coffee-beans-ground",
  "Instant Coffee": "reference.food.instant-coffee",
  "Coffee Pods": "reference.food.coffee-pods",
  Tea: "reference.food.tea",
  "Hot Chocolate & Speciality": "reference.food.hot-chocolate-speciality",
  "Coffee Syrups & Toppings": "reference.food.coffee-syrups-toppings",
  "Sugar & Whiteners": "reference.food.sugar-whiteners",
  "Fresh Soups": "reference.food.fresh-soups",
  "Ready Meals": "reference.food.ready-meals",
  "Prepared Sauces": "reference.food.prepared-sauces",
  "Fresh Pastry & Dough": "reference.food.fresh-pastry-dough",
  "Fresh Pasta": "reference.food.fresh-pasta",
  "Prepared Proteins": "reference.food.prepared-proteins",
  "Sandwich Fillers": "reference.food.sandwich-fillers",
  "Pre-Made Sandwiches": "reference.food.pre-made-sandwiches",
  "Plant Proteins": "reference.food.plant-proteins",
  "Tofu, Tempeh & Seitan": "reference.food.tofu-tempeh-seitan",
  "Other Plant Proteins": "reference.food.other-plant-proteins",
  "Vegan Dairy": "reference.food.vegan-dairy",
  "Vegan Baked & Prepared": "reference.food.vegan-baked-prepared",
  "Dairy-Free Milks": "reference.food.dairy-free-milks",
  "Gluten-Free": "reference.food.gluten-free",
  "Plant Baking & Misc": "reference.food.plant-baking-misc",
  "South Asian": "reference.food.south-asian",
  "East Asian": "reference.food.east-asian",
  "Middle Eastern & North African": "reference.food.middle-eastern-north-african",
  "Latin American & Caribbean": "reference.food.latin-american-caribbean",
  African: "reference.food.african",
  Crisps: "reference.food.crisps",
  "Nuts & Trail Mix": "reference.food.nuts-trail-mix",
  "Bars & Healthy Snacks": "reference.food.bars-healthy-snacks",
  "Chocolate Bars": "reference.food.chocolate-bars",
  "Biscuits & Cookies": "reference.food.biscuits-cookies",
  "Specialist Ingredients": "reference.food.specialist-ingredients",
  "Specialist Salts": "reference.food.specialist-salts",
  "Whole Spices": "reference.food.whole-spices",
  "Dried Herbs": "reference.food.dried-herbs",
  "Wrapping & Film": "reference.food.wrapping-film",
  "Takeaway Containers": "reference.food.takeaway-containers",
  "Cups & Lids": "reference.food.cups-lids",
  "Cutlery & Plates": "reference.food.cutlery-plates",
  "Napkins & Straws": "reference.food.napkins-straws",
  Bags: "reference.food.bags",
  Labels: "reference.food.labels",
  "Condiment Dispensers": "reference.food.condiment-dispensers",
  "Beer & Cider": "reference.food.beer-cider",
  Wine: "reference.food.wine",
  "Spirits & Liqueurs": "reference.food.spirits-liqueurs",
  Dairy: "reference.food.dairy",
  Protein: "reference.food.protein",
  Starch: "reference.food.starch",
  Vegetables: "reference.food.vegetables",
  Fruit: "reference.food.fruit",
  Bakery: "reference.food.bakery",
  Liquids: "reference.food.liquids",
  Mixed: "reference.food.mixed",
  Other: "reference.food.other",
  "Cream / Liquid": "reference.food.cream_liquid",
  Cheese: "reference.food.cheese",
  Yoghurt: "reference.food.yoghurt",
  Butter: "reference.food.butter",
  "Red Meat": "reference.food.red_meat",
  Poultry: "reference.food.poultry",
  Seafood: "reference.food.seafood",
  "Plant Protein": "reference.food.plant_protein",
  Rice: "reference.food.rice",
  Potato: "reference.food.potato",
  "Bread / Dough": "reference.food.bread_dough",
  Grains: "reference.food.grains",
  Root: "reference.food.root",
  "Leafy Green": "reference.food.leafy_green",
  Brassica: "reference.food.brassica",
  Allium: "reference.food.allium",
  "Mixed Veg": "reference.food.mixed_veg",
  Berry: "reference.food.berry",
  "Mixed Fruit": "reference.food.mixed_fruit",
  Bread: "reference.food.bread",
  Pastry: "reference.food.pastry",
  Cake: "reference.food.cake",
  Biscuit: "reference.food.biscuit",
  Soup: "reference.food.soup",
  Sauce: "reference.food.sauce",
  Beverage: "reference.food.beverage",
  "Oil / Fat": "reference.food.oil_fat",
};

/**
 * The reader's wording for a food name, falling back to the name as stored.
 *
 * Nullable in, `undefined` out, because most callers read a nullable wire field and pick the
 * first name they have — `foodLabel(t, item) ?? foodLabel(t, sub) ?? foodLabel(t, cat)`. The
 * overload keeps a known-present name typed as `string`, so the picker and the chart axis don't
 * each carry a `?? name` that can never fire.
 */
export function foodLabel(t: TFunction, name: string): string;
export function foodLabel(t: TFunction, name: string | null | undefined): string | undefined;
export function foodLabel(t: TFunction, name: string | null | undefined): string | undefined {
  if (!name) return undefined;
  const labelKey = FOOD_LABEL_KEYS[name];
  return labelKey ? t(labelKey) : name;
}

/**
 * The four ids a report line carries beside its label, naming which of them produced it.
 *
 * `waste_event_line_stats` builds `line_label` by walking recipe → item → subcategory → category
 * and falling through to the literal `'Unidentified'`, and it returns each id alongside. So the
 * client already knows *what kind of thing* the label is, and has since the RPC shipped.
 */
export interface LineIdentity {
  label: string;
  recipe_id: string | null;
  inventory_item_id: string | null;
  subcategory_id: string | null;
  category_id: string | null;
}

/**
 * The reader's wording for a report line, decided by the id that produced the label rather than by
 * whether `FOOD_LABEL_KEYS` happens to know the string (POS2-1267).
 *
 * This is the structural version of the rule `foodLabel` can only approximate. `foodLabel` is
 * keyed on the English name because the endpoints it serves flatten the join and send no ids, so
 * it translates anything it recognises and passes everything else through — which means a
 * catalogue item an organisation happened to name "Beef" gets translated as though it were the
 * taxonomy entry. A line knows better, and should use what it knows:
 *
 * - **A recipe or an inventory item is tenant text.** An organisation's own naming, and the AI's
 *   `item` label is open vocabulary. Returned exactly as stored, in every locale.
 * - **A subcategory or category is the fixed taxonomy.** 191 seeded rows, translated by key.
 * - **No id at all is the `'Unidentified'` fallthrough.** Not a food name and correctly absent from
 *   `FOOD_LABEL_KEYS`, so it is rendered from `reference.tier.l1` — which already says exactly this
 *   in both locales — rather than by recognising an English literal the RPC happens to emit.
 */
export function lineLabel(t: TFunction, line: LineIdentity): string {
  if (line.recipe_id || line.inventory_item_id) return line.label;
  if (line.subcategory_id || line.category_id) return foodLabel(t, line.label);
  return t("reference.tier.l1");
}
